<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import DeskCollapse from '@ticket-manager-ap/components/ticket-show-desk/components/DeskCollapse.vue';
import DeskAjaxSelect from './DeskAjaxSelect.vue';

const props = defineProps({
    ticketId: { type: [String, Number], required: true },
    canOperate: { type: Boolean, default: false },
    customerEmail: { type: String, default: '' },
});

const labels = {
    linkedOrders: 'Ordini collegati',
    noLinkedOrders: 'Nessun ordine collegato',
    linkOrder: 'Collega ordine…',
    searchOrderNumber: 'Numero ordine',
    searchOrderEmail: 'Email',
    confirmLinkOrder: 'Collega',
    unlinkOrder: 'Sgancia',
    order: 'Ordine',
};

const expanded = ref(false);
const panelOpen = ref(false);
const mode = ref('number');
const selectedId = ref('');
const selectedLabel = ref('');
const selectedHit = ref(null);
const orders = ref([]);
const loading = ref(false);
const busy = ref(false);
const ticketOpen = ref(true);

const listUrl = computed(() => `/tickets/${props.ticketId}/orders`);
const searchUrl = computed(() => `/tickets/${props.ticketId}/orders/search`);
const linkUrl = computed(() => `/tickets/${props.ticketId}/orders/link`);
const canLink = computed(() => Boolean(props.canOperate && ticketOpen.value));
const emailPrefill = computed(() => (props.customerEmail || '').trim());

const csrfToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
};

const orderLabel = (order) => {
    const ref = order.order_ref || order.number || order.id;
    return `#${ref}`;
};

const sourceLabel = (source) => (source === 'manual' ? 'manuale' : 'arricchimento');

const formatPortal = (portal) => (portal && portal !== 'dlm' ? portal : 'DLM shop');

const loadOrders = async () => {
    loading.value = true;
    try {
        const response = await fetch(listUrl.value, {
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });
        const data = await response.json().catch(() => []);
        orders.value = Array.isArray(data) ? data : [];
    } catch {
        orders.value = [];
    } finally {
        loading.value = false;
    }
};

watch(mode, () => {
    selectedId.value = '';
    selectedLabel.value = '';
    selectedHit.value = null;
});

watch(() => props.ticketId, () => {
    panelOpen.value = false;
    loadOrders();
});

const onSelect = (hit) => {
    selectedHit.value = hit;
};

const openPanel = () => {
    panelOpen.value = true;
};

const submitLink = async () => {
    if (!selectedHit.value && (selectedId.value === '' || selectedId.value === null || selectedId.value === undefined)) {
        return;
    }
    const hit = selectedHit.value || { id: selectedId.value, entity_id: null };
    busy.value = true;
    try {
        const response = await fetch(linkUrl.value, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken(),
            },
            body: JSON.stringify({
                num_prenotazione: Number(hit.id),
                entity_id: hit.entity_id || hit.entityId || null,
            }),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && Array.isArray(data.linked_orders)) {
            orders.value = data.linked_orders;
            panelOpen.value = false;
            selectedId.value = '';
            selectedLabel.value = '';
            selectedHit.value = null;
        }
    } finally {
        busy.value = false;
    }
};

const unlinkOrder = async (order) => {
    if (!order?.id || !canLink.value) {
        return;
    }
    busy.value = true;
    try {
        const response = await fetch(`/tickets/${props.ticketId}/orders/${order.id}`, {
            method: 'DELETE',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken(),
            },
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok && Array.isArray(data.linked_orders)) {
            orders.value = data.linked_orders;
        }
    } finally {
        busy.value = false;
    }
};

onMounted(() => {
    loadOrders();
});
</script>

<template>
    <section
        class="ticket-desk-panel ticket-desk-orders"
        :class="{ 'ticket-desk-panel--collapsed': !expanded }"
        data-testid="ticket-desk-linked-orders"
    >
        <button
            type="button"
            class="ticket-desk-panel-heading ticket-desk-panel-heading--toggle"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
        >
            <h3>
                <span aria-hidden="true">▰</span>
                {{ labels.linkedOrders }}
            </h3>
            <span class="ticket-desk-panel-heading__side">
                <span class="ticket-desk-badge ticket-desk-badge--outline">{{ orders.length }}</span>
                <span class="ticket-desk-chevron" aria-hidden="true">▾</span>
            </span>
        </button>
        <DeskCollapse :expanded="expanded">
            <div class="ticket-desk-orders__body">
                <div class="ticket-desk-orders__list">
                    <article
                        v-for="(order, index) in orders"
                        :key="order.id || order.order_ref || index"
                        class="ticket-desk-order"
                    >
                        <div class="ticket-desk-order__head">
                            <strong>{{ orderLabel(order) }}</strong>
                            <div class="ticket-desk-order__badges">
                                <span class="ticket-desk-order__badge">{{ sourceLabel(order.source) }}</span>
                                <span
                                    v-if="order.is_return"
                                    class="ticket-desk-order__badge ticket-desk-order__badge--return"
                                >
                                    reso
                                </span>
                            </div>
                        </div>
                        <p v-if="order.customer_name || order.customer_email" class="ticket-desk-order__meta">
                            {{ order.customer_name || '—' }}
                            <template v-if="order.customer_email"> · {{ order.customer_email }}</template>
                        </p>
                        <p v-if="order.reserved_at_label || order.deposit_paid_label" class="ticket-desk-order__meta">
                            <template v-if="order.reserved_at_label">{{ order.reserved_at_label }}</template>
                            <template v-if="order.deposit_paid_label"> · {{ order.deposit_paid_label }}</template>
                            <template v-if="order.source_portal"> · {{ formatPortal(order.source_portal) }}</template>
                        </p>
                        <ul v-if="order.lines && order.lines.length" class="ticket-desk-order__lines">
                            <li v-for="(line, lineIndex) in order.lines" :key="lineIndex">
                                <span>{{ line.quantity }}× {{ line.title }}</span>
                                <span v-if="line.ean">{{ line.ean }}</span>
                            </li>
                        </ul>
                        <p v-if="order.shipment && (order.shipment.tracking_number || order.shipment.dispatch_date_label)" class="ticket-desk-order__shipment">
                            Spedizione
                            <template v-if="order.shipment.dispatch_date_label"> · {{ order.shipment.dispatch_date_label }}</template>
                            <template v-if="order.shipment.tracking_number"> · {{ order.shipment.tracking_number }}</template>
                        </p>
                        <p v-if="order.load_error" class="ticket-desk-order__error">
                            Dettaglio ordine non disponibile
                        </p>
                        <div v-if="canLink" class="ticket-desk-order__actions">
                            <button
                                type="button"
                                class="ticket-desk-button ticket-desk-button--link"
                                data-testid="ticket-desk-unlink-order"
                                :disabled="busy"
                                @click="unlinkOrder(order)"
                            >
                                {{ labels.unlinkOrder }}
                            </button>
                        </div>
                    </article>
                </div>
                <p v-if="!loading && !orders.length" class="ticket-desk-empty">{{ labels.noLinkedOrders }}</p>

                <button
                    v-if="canLink && !panelOpen"
                    type="button"
                    class="ticket-desk-button ticket-desk-button--link ticket-desk-orders__link"
                    data-testid="ticket-desk-open-link-order"
                    @click="openPanel"
                >
                    🔗 {{ labels.linkOrder }}
                </button>

                <div
                    v-if="canLink && panelOpen"
                    class="ticket-desk-orders__link-panel"
                    data-testid="ticket-desk-link-order-panel"
                >
                    <div class="ticket-desk-orders__modes" role="tablist">
                        <button
                            type="button"
                            class="ticket-desk-button ticket-desk-button--outline"
                            :class="{ 'is-active': mode === 'number' }"
                            role="tab"
                            :aria-selected="mode === 'number'"
                            @click="mode = 'number'"
                        >
                            {{ labels.searchOrderNumber }}
                        </button>
                        <button
                            type="button"
                            class="ticket-desk-button ticket-desk-button--outline"
                            :class="{ 'is-active': mode === 'email' }"
                            role="tab"
                            :aria-selected="mode === 'email'"
                            @click="mode = 'email'"
                        >
                            {{ labels.searchOrderEmail }}
                        </button>
                    </div>

                    <DeskAjaxSelect
                        :key="mode"
                        v-model="selectedId"
                        v-model:selected-label="selectedLabel"
                        :search-url="searchUrl"
                        :mode="mode"
                        :labels="labels"
                        :initial-query="mode === 'email' ? emailPrefill : ''"
                        :placeholder="mode === 'email' ? labels.searchOrderEmail : labels.searchOrderNumber"
                        @select="onSelect"
                        @clear="selectedHit = null"
                    />

                    <button
                        type="button"
                        class="ticket-desk-button ticket-desk-button--primary ticket-desk-orders__confirm"
                        data-testid="ticket-desk-confirm-link-order"
                        :disabled="busy || selectedId === '' || selectedId === null || selectedId === undefined"
                        @click="submitLink"
                    >
                        {{ labels.confirmLinkOrder }}
                    </button>
                </div>
            </div>
        </DeskCollapse>
    </section>
</template>
